import React from "react"
import Box from "@material-ui/core/Box"
import Grid from "@material-ui/core/Grid"
import Paper from "@material-ui/core/Paper"
import Typography from "@material-ui/core/Typography"
import { Chip, FormControl, ListItemText, makeStyles, MenuItem, OutlinedInput, Select, Theme } from "@material-ui/core"
import { ChevronDown } from "react-feather"
import { keyBy } from "lodash"
import {
  DATE_ORDER_OPTIONS,
  DateOrderOptionType,
  DateSeparatorOptionType,
  DEFAULT_DATE_ORDER,
  DEFAULT_DATE_SEPARATOR,
} from "../utils/date-format"
import { useTranslation } from "react-i18next"

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8

const useDateOrderOptions = (dateFormatSeparator: DateSeparatorOptionType) => {
  const { t } = useTranslation()
  const dayLabel = t("dateFormatSelectWidget.dayLabel", "Day")
  const monthLabel = t("dateFormatSelectWidget.monthLabel", "Month")
  const yearLabel = t("dateFormatSelectWidget.yearLabel", "Year")

  return DATE_ORDER_OPTIONS.map((dateFormat: DateOrderOptionType) => {
    if (dateFormat === "DD.MM.YYYY")
      return {
        value: dateFormat,
        label: `${dayLabel}${dateFormatSeparator}${monthLabel}${dateFormatSeparator}${yearLabel}`,
      }
    if (dateFormat === "MM.DD.YYYY")
      return {
        value: dateFormat,
        label: `${monthLabel}${dateFormatSeparator}${dayLabel}${dateFormatSeparator}${yearLabel}`,
      }
    return {
      value: dateFormat,
      label: `${yearLabel}${dateFormatSeparator}${monthLabel}${dateFormatSeparator}${dayLabel}`,
    }
  })
}

const DateFormatSelectWidget = ({
  dateFormat = DEFAULT_DATE_ORDER,
  dateFormatSeparator = DEFAULT_DATE_SEPARATOR,
  onDateFormatChange = () => {},
  disabled = false,
}: DateFormatSelectWidgetProps) => {
  const classes = useStyles()
  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    onDateFormatChange({
      dateFormat: event.target.value as DateOrderOptionType,
    })
  }
  const selectorOptions = useDateOrderOptions(dateFormatSeparator)
  const { t } = useTranslation("translation")

  return (
    <Paper elevation={0}>
      <Box p={4}>
        <Grid container direction="column">
          <Typography variant="subtitle1">{t("dateFormatSelectWidget.dateFormatTitle", "Date format")}</Typography>
          <Grid item>
            <FormControl className={classes.formControl} disabled={disabled}>
              <Select
                labelId="tz-select"
                id="tz-select"
                value={dateFormat}
                onChange={handleChange}
                input={<DateOrderInput />}
                MenuProps={MenuProps}
                IconComponent={ChevronDown}
                renderValue={(selected: any) => {
                  const selectorOptionsMap = keyBy(selectorOptions, "value")
                  return (
                    <div className={classes.chips}>
                      <Chip label={selectorOptionsMap[selected].label} className={classes.chip} />
                    </div>
                  )
                }}
              >
                {selectorOptions.map(({ value, label }, index) => (
                  <MenuItem key={index} value={value}>
                    <ListItemText primary={label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  )
}

const DateOrderInput = (props: any) => {
  const classes = useStyles()
  return <OutlinedInput {...props} className={classes.dateFormatInput} />
}
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      minWidth: 250,
    },
  },
}

const useStyles = makeStyles((theme: Theme) => ({
  formControl: {},
  dateFormatInput: {
    "& .MuiOutlinedInput-input": {
      paddingTop: 8,
      paddingBottom: 8,
    },
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
  },
  chip: {
    margin: 2,
    borderRadius: 2,
    background: `${theme.palette.primary.main}21`,
  },
}))

type DateFormatSelectWidgetProps = {
  onDateFormatChange?: ({ dateFormat }: { dateFormat: DateOrderOptionType }) => void
  dateFormat?: DateOrderOptionType
  dateFormatSeparator?: DateSeparatorOptionType
  disabled?: boolean
}

export default DateFormatSelectWidget
